/**
 * WordPress dependencies
 */
import { Path, SVG } from '@safe-wordpress/components';
import { useEffect, useState } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import './style.scss';

import { useVisualEditorState, usePropManager } from '../../hooks';
import { ColorPanel } from './color-panel';
import { DimensionsPanel } from './dimensions-panel';
import { TypographyPanel } from './typography-panel';
import { BorderPanel } from './border-panel';
import { DisplayPanel } from './display-panel';
import { PositioningPanel } from './positioning-panel';
import { FiltersPanel } from './filters-panel';
import { SelectorPanel } from './selector-panel';
import { TransformationsPanel } from './transformations-panel';

export const VisualEditor = (): JSX.Element | null => {
	const state = useVisualEditorState();
	const onChange = usePropManager();

	const isRenderingPrevented = useRenderingPrevention();
	if ( isRenderingPrevented ) {
		return null;
	}

	if ( ! state ) {
		return (
			<div className="nab-css-editor-sidebar__visual-editor nab-css-editor-sidebar__visual-editor--empty">
				<SelectionIcon />
				<p>
					{ _x(
						'Select any element on the page to start making changes.',
						'user',
						'nelio-ab-testing'
					) }
				</p>
			</div>
		);
	}

	if ( ! state.details ) {
		return (
			<div className="nab-css-editor-sidebar__visual-editor nab-css-editor-sidebar__visual-editor--empty">
				<ErrorIcon />
				<p>
					{ _x(
						'There’s something wrong with your CSS rules. Please review the CSS Editor tab.',
						'user',
						'nelio-ab-testing'
					) }
				</p>
			</div>
		);
	}

	const { props } = state.details;
	return (
		<div className="nab-css-editor-sidebar__visual-editor">
			<SelectorPanel />
			<ColorPanel value={ props } onChange={ onChange } />
			<TypographyPanel value={ props } onChange={ onChange } />
			<DimensionsPanel value={ props } onChange={ onChange } />
			<BorderPanel value={ props } onChange={ onChange } />
			<DisplayPanel value={ props } onChange={ onChange } />
			<PositioningPanel value={ props } onChange={ onChange } />
			<FiltersPanel value={ props } onChange={ onChange } />
			<TransformationsPanel value={ props } onChange={ onChange } />
		</div>
	);
};

const SelectionIcon = (): JSX.Element => (
	<SVG
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		width="48"
		height="48"
	>
		<Path d="M11.5,11L17.88,16.37L17,16.55L16.36,16.67C15.73,16.8 15.37,17.5 15.65,18.07L15.92,18.65L17.28,21.59L15.86,22.25L14.5,19.32L14.24,18.74C13.97,18.15 13.22,17.97 12.72,18.38L12.21,18.78L11.5,19.35V11M10.76,8.69A0.76,0.76 0 0,0 10,9.45V20.9C10,21.32 10.34,21.66 10.76,21.66C10.95,21.66 11.11,21.6 11.24,21.5L13.15,19.95L14.81,23.57C14.94,23.84 15.21,24 15.5,24C15.61,24 15.72,24 15.83,23.92L18.59,22.64C18.97,22.46 19.15,22 18.95,21.63L17.28,18L19.69,17.55C19.85,17.5 20,17.43 20.12,17.29C20.39,16.97 20.35,16.5 20,16.21L11.26,8.86L11.25,8.87C11.12,8.76 10.95,8.69 10.76,8.69M15,10V8H20V10H15M13.83,4.76L16.66,1.93L18.07,3.34L15.24,6.17L13.83,4.76M10,0H12V5H10V0M3.93,14.66L6.76,11.83L8.17,13.24L5.34,16.07L3.93,14.66M3.93,3.34L5.34,1.93L8.17,4.76L6.76,6.17L3.93,3.34M7,10H2V8H7V10" />
	</SVG>
);

const ErrorIcon = (): JSX.Element => (
	<SVG
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 24 24"
		width="48"
		height="48"
	>
		<Path d="M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z" />
	</SVG>
);

// HACK: Make sure the visual editor is completely refreshed when the selected
// element changes. This way, we can guarantee that only the appropriate
// components are expanded.
const useRenderingPrevention = () => {
	const [ isRenderingPrevented, setRenderingPrevention ] = useState( false );
	const state = useVisualEditorState();
	useEffect( () => {
		setRenderingPrevention( true );
		setTimeout( () => setRenderingPrevention( false ), 0 );
	}, [ state?.activeElement?.selector, state?.activeElement?.index ] );
	return isRenderingPrevented;
};
